import { useCallback, useEffect, useState } from "react";
import useDebounce from "./useDebounce";

interface UseTableType {
  defaultPage?: number;
  defaultLimit?: number;
  hideSkipLimit?: boolean;
}

export default function useTable<T>(props?: UseTableType) {
  const defaultPage = props?.defaultPage || 1,
    defaultLimit = props?.defaultPage || 10;

  const [page, setPage] = useState(defaultPage);
  const [limit, setLimit] = useState(defaultLimit);
  const [isResetPagination, setIsResetPagination] = useState(false);
  const { keyword, handleInputChange: handleSearch } = useDebounce();
  const [filter, setFilter] = useState<T>();

  const handleLimit = (val: number) => {
    setLimit(Number(val));
  };

  const handlePagination = useCallback(
    (paginationPage: number, paginationLimit: number) => {
      setPage(paginationPage);
      setLimit(paginationLimit);
    },
    []
  );

  const resetPagination = useCallback(() => {
    setIsResetPagination(true);
  }, []);

  // When filtering, redirect table in first page
  useEffect(() => {
    if (isResetPagination) {
      handlePagination(defaultPage, defaultLimit);
      setIsResetPagination(false);
    }
  }, [handlePagination, isResetPagination, defaultLimit, defaultPage]);

  // handle filter
  const handleFilter = <K extends keyof T>(filterType: K, value: T[K]) => {
    setFilter((prev) =>
      prev
        ? ({
            ...prev,
            [filterType]: value,
          } as T)
        : ({
            [filterType]: value,
          } as T)
    );

    resetPagination();
  };

  // reset pagination when search
  useEffect(() => {
    if (keyword) resetPagination();
  }, [keyword, resetPagination]);

  return {
    page,
    limit,
    handleLimit,
    handlePagination,
    resetPagination,
    keyword,
    filter: {
      ...(!props?.hideSkipLimit ? { limit, page } : {}),
      ...filter,
    } as T,
    handleSearch,
    handleFilter,
  };
}
